import React, { useEffect, useState } from 'react';
import { SypacInput } from '@sypac/component-library-react';
import { cn } from '../../../../lib/utils';

const FormInput: React.FC<{
	name: string;
	placeholder?: string;
	readOnly?: boolean;
	className?: string;
	formik: any;
}> = ({ name, placeholder, readOnly = false, className = '', formik }) => {
	const [localValue, setLocalValue] = useState<string>('');
	const [isFocused, setIsFocused] = useState<boolean>(false);

	const MAX_DECIMALS = 10;

	const clampToMaxDecimals = (value: string) => {
		if (value === '' || value === '.') return value;

		const dotIndex = value.indexOf('.');
		if (dotIndex === -1) return value;

		const intPart = value.slice(0, dotIndex);
		const decPart = value.slice(dotIndex + 1);

		return `${intPart}.${decPart.slice(0, MAX_DECIMALS)}`;
	};

	const getNestedValue = (obj: any, path: string) => {
		return path.split('.').reduce((curr, prop) => {
			if (prop.includes('[') && prop.includes(']')) {
				const matches = prop.match(/(\w+)(\[\d+])+/);
				if (matches) {
					const arrayName = matches[1];
					const indices =
						prop.match(/\[(\d+)]/g)?.map((m) => parseInt(m.slice(1, -1))) || [];

					let result = curr?.[arrayName];
					for (const index of indices) {
						result = result?.[index];
					}
					return result;
				}

				const [arrayName, index] = prop.split('[');
				const indexNum = parseInt(index.replace(']', ''));
				return curr?.[arrayName]?.[indexNum];
			}
			return curr?.[prop];
		}, obj);
	};

	const isNumericField =
		name.includes('plnValues') ||
		name === 'margin' ||
		name.includes('distances') ||
		name.includes('categories');

	const rawValue = getNestedValue(formik.values, name) || '';

	useEffect(() => {
		if (!isFocused) {
			if (isNumericField && rawValue !== '') {
				const numStr = clampToMaxDecimals(String(rawValue));

				if (/^\d+$/.test(numStr)) {
					setLocalValue(`${numStr}.0`);
				} else {
					setLocalValue(numStr);
				}
			} else {
				setLocalValue(String(rawValue));
			}
		}
	}, [rawValue, isNumericField, isFocused]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;

		if (isNumericField) {
			if (inputValue === '') {
				setLocalValue('');
				formik.setFieldValue(name, '');
				return;
			}

			if (/^\d*\.?\d*$/.test(inputValue)) {
				if (inputValue.match(/^0[0-9]/)) {
					return;
				}

				const nextValue = clampToMaxDecimals(inputValue);

				setLocalValue(nextValue);

				const valueToStore =
					nextValue.endsWith('.') && nextValue !== '.'
						? nextValue.slice(0, -1)
						: nextValue;

				formik.setFieldValue(name, valueToStore);
			}
		} else {
			setLocalValue(inputValue);
			formik.setFieldValue(name, inputValue);
		}
	};

	const handleFocus = () => {
		setIsFocused(true);
		if (isNumericField && localValue.endsWith('.0')) {
			const baseValue = localValue.slice(0, -2);
			setLocalValue(baseValue);
		}
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		setIsFocused(false);

		if (isNumericField && localValue !== '') {
			let finalValue = localValue;

			if (finalValue.endsWith('.')) {
				finalValue = finalValue.slice(0, -1);
			}

			finalValue = clampToMaxDecimals(finalValue);
			formik.setFieldValue(name, finalValue);

			if (/^\d+$/.test(finalValue)) {
				setLocalValue(`${finalValue}.0`);
			} else {
				setLocalValue(finalValue);
			}
		}

		formik.handleBlur(e);
	};

	const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (isNumericField) {
			const char = String.fromCharCode(e.which);
			const currentValue = (e.target as HTMLInputElement).value;

			const input = e.target as HTMLInputElement;
			const { selectionStart, selectionEnd } = input;

			if (/[0-9]/.test(char) && currentValue.includes('.')) {
				const dotIndex = currentValue.indexOf('.');
				const cursorPos = selectionStart ?? currentValue.length;
				const replacing = (selectionStart ?? 0) !== (selectionEnd ?? 0);

				if (cursorPos > dotIndex) {
					const decimalsNow = currentValue.slice(dotIndex + 1);
					if (!replacing && decimalsNow.length >= MAX_DECIMALS) {
						e.preventDefault();
						return;
					}
				}
			}

			if ([8, 9, 27, 13, 46].indexOf(e.which) !== -1) {
				return;
			}

			if (char === '.' && currentValue.indexOf('.') === -1) {
				return;
			}

			if (!/[0-9]/.test(char)) {
				e.preventDefault();
			}
		}
	};

	const touched = getNestedValue(formik.touched, name);
	const error = getNestedValue(formik.errors, name);

	return (
		<SypacInput error={!!(touched && error)} className="relative w-full">
			<input
				readOnly={readOnly}
				className={cn(
					'py-2.5 pl-3 border rounded-[8px] placeholder:text-gray-22',
					className,
					readOnly && 'pointer-events-none',
					error && 'bg-white',
				)}
				name={name}
				type="text"
				placeholder={placeholder}
				value={localValue}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onKeyPress={handleKeyPress}
			/>
			{name === 'margin' && (
				<span className="absolute right-3 top-[20px] transform -translate-y-1/2 text-gray-400 pointer-events-none">
					%
				</span>
			)}
			{touched && error ? (
				<span className="input-error -tracking-[10%]">{error}</span>
			) : null}
		</SypacInput>
	);
};

export default FormInput;
